import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text, Card, Stack, Group, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useAuthStore } from '../store/authSlice';
import { useAccess } from '../access/AccessContext';
import { PermissionControlled } from '../components/permissions';
import { PageContainer } from '../components/PageContainer';
import { usePermissions } from '../hooks/usePermissions';
import { PluginRegistry } from '../plugins';

interface MenuItemProps {
  icon: React.ComponentType<any>;
  label: string;
  description?: string;
  to: string;
  color?: string;
}

function MenuItem({ icon: Icon, label, description, to, color = 'blue' }: MenuItemProps) {
  const navigate = useNavigate();
  
  return (
    <UnstyledButton onClick={() => navigate(to)} style={{ width: '100%' }}>
      <Card withBorder p="md">
        <Group justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <ThemeIcon size="lg" variant="light" color={color} style={{ flexShrink: 0 }}>
              <Icon size={20} />
            </ThemeIcon>
            <div style={{ minWidth: 0 }}>
              <Text fw={500} style={{ fontSize: 'var(--font-size-sm)' }}>{label}</Text>
              {description && <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{description}</Text>}
            </div>
          </Group>
          <IconChevronRight size={18} color="gray" style={{ flexShrink: 0 }} />
        </Group>
      </Card>
    </UnstyledButton>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { isAdmin } = useAccess();
  usePermissions();

  // Get widgets from all registered plugins
  const widgets = PluginRegistry.getDashboardWidgets();

  // Group widgets by plugin
  const widgetsByPlugin = widgets.reduce((acc, widget) => {
    if (!acc[widget.pluginId]) {
      acc[widget.pluginId] = { name: widget.pluginName, widgets: [] };
    }
    acc[widget.pluginId].widgets.push(widget);
    return acc;
  }, {} as Record<string, { name: string; widgets: typeof widgets }>);

  return (
    <PageContainer>
      <Stack gap="md">
        <Card withBorder>
          <Text fw={500} style={{ fontSize: 'var(--font-size-md)' }}>
            {t('dashboard.welcome')}, {user?.first_name || user?.login}!
          </Text>
        </Card>

        {Object.entries(widgetsByPlugin).map(([pluginId, { name, widgets: pluginWidgets }]) => (
          <Stack key={pluginId} gap="xs">
            <Text fw={500} c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{name}</Text>
            {pluginWidgets.map((widget) => {
              if (widget.requireAdmin && !isAdmin) return null;
              
              const permPath = `${pluginId}.${widget.path.split('/').filter(Boolean).join('.')}`;
              
              return (
                <PermissionControlled key={widget.path} path={permPath}>
                  <MenuItem
                    icon={widget.icon}
                    label={widget.title}
                    description={widget.description}
                    to={widget.path}
                    color={widget.color}
                  />
                </PermissionControlled>
              );
            })}
          </Stack>
        ))}
      </Stack>
    </PageContainer>
  );
}

export default DashboardPage;
